import {makeAutoObservable, runInAction} from 'mobx';
import {getProduct} from '../http/catalogApi';
import {getSizeCommerceView} from '../utils/onecStockAvailability';
import {validateBasketSession} from '../utils/siteBasketSnapshot';
import {isServerSnapshotTimeValid} from '../utils/serverSnapshotTime';
import {canDraftAutomaticPreorder} from '../pages/basket/PreorderCart/AutomaticPreorderSelection';

const ERROR = 'Не удалось сохранить предзаказ в корзине. Попробуйте ещё раз.';
const REFRESH_ERROR = 'Не удалось обновить товары предзаказа. Попробуйте ещё раз.';
const SCHEMA = 'preorder-cart-selection-v1';
const MAX_LINES = 100;
const MAX_QUANTITY = 999;
const id = value => Number.isSafeInteger(value) && value > 0 && value <= 2147483647;
const quantity = value => Number.isSafeInteger(value) && value > 0 && value <= MAX_QUANTITY;
const revision = value => Number.isSafeInteger(value) && value > 0;
const transferKeyValid = value => typeof value === 'string' &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
const newTransferKey = () => {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 15) | 64; bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};
const routePart = value => typeof value === 'string' && value.length > 0 && value.length <= 255 && !/[\u0000-\u0020/\\?#]/.test(value);
const lineKey = identity => `preorder:${identity.childProductId}:${identity.colorId}:${identity.sizeId}`;
const storageKey = session => `yw:preorder-cart:v1:${session.authScope}:${session.principalId ?? 'guest'}`;
const sameOwner = (first, second) => first && second && first.authScope === second.authScope && first.principalId === second.principalId;
const identityOnly = value => ({childProductId: value.childProductId, colorId: value.colorId, sizeId: value.sizeId,
  code: value.code, parentCategory: value.parentCategory ?? null, childCategory: value.childCategory ?? null});
const validIdentity = value => value && id(value.childProductId) && id(value.colorId) && id(value.sizeId) && routePart(value.code) &&
  (value.parentCategory == null || routePart(value.parentCategory)) && (value.childCategory == null || routePart(value.childCategory));
const storedLine = line => ({identity: identityOnly(line.identity), quantity: line.quantity, revision: line.revision,
  ...(line.transferKey ? {transferKey: line.transferKey} : {}),
  ...(line.transferSizeName ? {transferSizeName: line.transferSizeName} : {}),
  ...(line.transferTargetCount !== undefined ? {transferTargetCount: line.transferTargetCount} : {})});
const restoredLine = line => ({...storedLine(line), lineKey: lineKey(line.identity), item: null, currentSize: null,
  verification: 'unverified', createdRevision: line.revision, lastDecreaseRevision: 0});

// A public-card hint may start a selection, never an order. Both checkout
// routes independently revalidate the selection before accepting an Order.
export function canDraftPreorder(item, sizeName, count = 1) {
  if (process.env.REACT_APP_AUTOMATIC_PREORDER_SITE_CHECKOUT_ENABLED === 'true')
    return canDraftAutomaticPreorder(item, sizeName, count);
  const view = getSizeCommerceView(item, sizeName, count), row = view.descriptor;
  return Boolean(quantity(count) && item?.is_active === true && item.product?.active_product === true &&
    item.product?.electron_sertificate !== true && id(item.id) && id(item.colorId) && routePart(item.code) &&
    view.mode === 'enforced' && row?.kind === 'ordinary' && !view.available &&
    isServerSnapshotTimeValid(item?.commerce?.evaluatedAt, Date.now(), 5 * 60 * 1000) &&
    ['preorders_disabled', 'preorder_ready', 'preorder_quota_freshness_invalid'].includes(row.reasonCode) &&
    (row.action === 'preorder' ? row.checkoutAllowed && count <= row.maxQuantity : row.action === 'notify'));
}

function hydrated(line, item) {
  const name = item?.child_product_size_field?.find(field => (field.sizeId ?? field.id) === line.identity.sizeId)?.size_name;
  const current = getSizeCommerceView(item, name, line.quantity), descriptor = current.descriptor;
  if (item?.id === line.identity.childProductId && item.colorId === line.identity.colorId &&
    descriptor?.sizeId === line.identity.sizeId && current.action === 'buy_now' && current.available &&
    descriptor.kind === 'ordinary' && descriptor.stock?.state === 'confirmed' &&
    descriptor.stock.quantity >= line.quantity)
    return {...line, item, currentSize: name, verification: 'available'};
  if (!canDraftPreorder(item, name, line.quantity)) return {...line, item: null, currentSize: null, verification: 'unavailable'};
  const row = getSizeCommerceView(item, name).descriptor;
  if (item.id !== line.identity.childProductId || item.colorId !== line.identity.colorId || row.sizeId !== line.identity.sizeId) {
    return {...line, item: null, currentSize: null, verification: 'unavailable'};
  }
  return {...line, item, currentSize: name, verification: !line.transferKey && row.action === 'preorder' && row.checkoutAllowed ? 'verified' : 'unverified'};
}

export default class PreorderCartStore {
  lines = [];
  session = null;
  epoch = 0;
  revision = 0;
  pending = false;
  error = null;
  _guestHandoff = null;
  _request = 0;

  constructor({readProduct = getProduct, storage = window.localStorage, createTransferKey = newTransferKey} = {}) {
    this._readProduct = readProduct;
    this._storage = storage;
    this._createTransferKey = createTransferKey;
    makeAutoObservable(this, {_readProduct: false, _storage: false, _createTransferKey: false, _guestHandoff: false});
  }

  get storageKey() {return this.session ? storageKey(this.session) : null;}
  get count() {return this.lines.reduce((sum, line) => sum + line.quantity, 0);}

  _read(session) {
    const raw = this._storage.getItem(storageKey(session));
    if (!raw) return [];
    if (raw.length > 100000) throw new Error(ERROR);
    const value = JSON.parse(raw);
    if (value.schemaVersion !== SCHEMA || !sameOwner(value, session) || !Array.isArray(value.lines) || value.lines.length > MAX_LINES) throw new Error(ERROR);
    const seen = new Set();
    return value.lines.map(line => {
      if (!validIdentity(line?.identity) || !quantity(line.quantity) || !revision(line.revision) || seen.has(lineKey(line.identity)) ||
        (line.transferKey !== undefined && !transferKeyValid(line.transferKey)) ||
        (line.transferKey !== undefined && (typeof line.transferSizeName !== 'string' ||
          !line.transferSizeName.trim() || line.transferSizeName.length > 128)) ||
        (line.transferSizeName !== undefined && !line.transferKey) ||
        (session.authScope === 'guest' && line.transferKey && line.transferTargetCount === undefined) ||
        (line.transferTargetCount !== undefined && (!line.transferKey || !Number.isSafeInteger(line.transferTargetCount) ||
          line.transferTargetCount < line.quantity || line.transferTargetCount > 2147483647))) throw new Error(ERROR);
      seen.add(lineKey(line.identity));
      return restoredLine(line);
    });
  }

  _save(lines) {
    if (!this.session) throw new Error(ERROR);
    this._storage.setItem(this.storageKey, JSON.stringify({schemaVersion: SCHEMA, ...this.session, lines: lines.map(storedLine)}));
  }

  beginSession(raw) {
    const session = validateBasketSession(raw);
    const handoff = this.session?.authScope === 'guest' ? this.lines.map(storedLine) : this._guestHandoff;
    this.epoch += 1; this._request += 1; this.session = session;
    this.lines = []; this.pending = false; this.error = null; this._guestHandoff = null;
    try {
      let lines = this._read(session);
      this.revision = Math.max(this.revision, 0, ...lines.map(line => line.revision));
      if (session.authScope === 'authenticated' && handoff?.length) {
        for (const selection of handoff) {
          const key = lineKey(selection.identity), existing = lines.find(line => line.lineKey === key);
          const count = (existing?.quantity ?? 0) + selection.quantity;
          if (!quantity(count) || (!existing && lines.length >= MAX_LINES)) throw new Error(ERROR);
          const merged = restoredLine({...selection, quantity: count, revision: ++this.revision});
          lines = existing ? lines.map(line => line.lineKey === key ? merged : line) : [...lines, merged];
        }
        // Consume the guest slot only after the account selection is durable.
        this._save(lines);
        this._storage.removeItem(storageKey({authScope: 'guest', principalId: null}));
      }
      this.lines = lines;
    } catch {this.error = ERROR;}
    return this.epoch;
  }

  suspendSession() {
    if (this.session?.authScope === 'guest') this._guestHandoff = this.lines.map(storedLine);
    else if (this.session?.authScope === 'authenticated') this._guestHandoff = null;
    this._clearSession();
  }

  _clearSession() {
    this.epoch += 1; this._request += 1; this.session = null; this.lines = []; this.pending = false; this.error = null;
  }

  disposeSession() {this._guestHandoff = null; this._clearSession();}

  add(item, sizeName, route = {}) {
    if (!this.session || !canDraftPreorder(item, sizeName)) return {status: 'failed', error: ERROR};
    const descriptor = getSizeCommerceView(item, sizeName).descriptor;
    const identity = identityOnly({childProductId: item.id, colorId: item.colorId, sizeId: descriptor.sizeId,
      code: item.code, parentCategory: route.parentCategory, childCategory: route.childCategory});
    if (!validIdentity(identity)) return {status: 'failed', error: ERROR};
    const key = lineKey(identity), previous = this.lines.find(line => line.lineKey === key);
    if (previous?.transferKey) return {status: 'failed', error: ERROR};
    const count = (previous?.quantity ?? 0) + 1;
    if (!canDraftPreorder(item, sizeName, count) || (!previous && this.lines.length >= MAX_LINES)) return {status: 'failed', error: ERROR};
    const nextRevision = this.revision + 1;
    const line = hydrated({...previous, identity, lineKey: key, quantity: count, revision: nextRevision,
      createdRevision: previous?.createdRevision ?? nextRevision, lastDecreaseRevision: previous?.lastDecreaseRevision ?? 0}, item);
    const lines = previous ? this.lines.map(value => value.lineKey === key ? line : value) : [...this.lines, line];
    try {this._save(lines);} catch {this.error = ERROR; return {status: 'failed', error: ERROR};}
    this.lines = lines; this.revision = nextRevision; this.error = null;
    return {status: 'added', line};
  }

  setQuantity(key, count) {
    const previous = this.lines.find(line => line.lineKey === key);
    if (!this.session || !previous || previous.transferKey || !quantity(count) || (previous.item && !canDraftPreorder(previous.item, previous.currentSize, count))) return false;
    if (previous.quantity === count) return true;
    const nextRevision = this.revision + 1;
    const lines = this.lines.map(line => line.lineKey === key ? {...line, quantity: count, revision: nextRevision,
      lastDecreaseRevision: count < line.quantity ? nextRevision : line.lastDecreaseRevision} : line);
    try {this._save(lines);} catch {this.error = ERROR; return false;}
    this.lines = lines; this.revision = nextRevision; this.error = null; return true;
  }

  remove(key) {
    if (!this.session || !this.lines.some(line => line.lineKey === key && !line.transferKey)) return false;
    const lines = this.lines.filter(line => line.lineKey !== key);
    try {this._save(lines);} catch {this.error = ERROR; return false;}
    this.lines = lines; this.revision += 1; this.error = null; return true;
  }

  prepareTransfer(key, targetCount = undefined) {
    const current = this.lines.find(line => line.lineKey === key);
    if (!this.session || !current) return null;
    if (current.transferKey && current.transferSizeName) return current;
    if (current.verification !== 'available' ||
      (this.session.authScope === 'guest' && (!Number.isSafeInteger(targetCount) ||
        targetCount < current.quantity || targetCount > 2147483647))) return null;
    let transferKey;
    try {transferKey = current.transferKey || this._createTransferKey();} catch {this.error = ERROR; return null;}
    if (!transferKeyValid(transferKey)) {this.error = ERROR; return null;}
    const line = {...current, transferKey, transferSizeName: current.currentSize, revision: this.revision + 1,
      ...(this.session.authScope === 'guest' ? {transferTargetCount: targetCount} : {})};
    const lines = this.lines.map(value => value.lineKey === key ? line : value);
    try {this._save(lines);} catch {this.error = ERROR; return null;}
    this.lines = lines; this.revision += 1; this.error = null;
    return line;
  }

  clearTransfer(key, transferKey) {
    const current = this.lines.find(line => line.lineKey === key);
    if (!this.session || !current || current.transferKey !== transferKey) return false;
    const {transferKey: _key, transferSizeName: _size, transferTargetCount: _count, ...rest} = current;
    const line = {...rest, verification: rest.item && canDraftPreorder(rest.item, rest.currentSize, rest.quantity) ? 'verified' : rest.verification,
      revision: this.revision + 1};
    const lines = this.lines.map(value => value.lineKey === key ? line : value);
    try {this._save(lines);} catch {this.error = ERROR; return false;}
    this.lines = lines; this.revision += 1; this.error = null; return true;
  }

  removeTransferred(key, transferKey, count) {
    const current = this.lines.find(line => line.lineKey === key);
    if (!this.session || !current || current.transferKey !== transferKey || current.quantity !== count) return false;
    const lines = this.lines.filter(line => line.lineKey !== key);
    try {this._save(lines);} catch {this.error = ERROR; return false;}
    this.lines = lines; this.revision += 1; this.error = null; return true;
  }

  captureSelection() {
    return {epoch: this.epoch, ...(this.session || {}), lines: this.lines.map(line => ({lineKey: line.lineKey, ...storedLine(line)}))};
  }

  removeAcceptedLines(snapshot) {
    if (!sameOwner(snapshot, this.session) || snapshot.epoch !== this.epoch || !Array.isArray(snapshot.lines)) return false;
    let nextRevision = this.revision;
    const lines = this.lines.flatMap(line => {
      const accepted = snapshot.lines.find(value => value.lineKey === line.lineKey);
      if (!accepted || !quantity(accepted.quantity) || !revision(accepted.revision) ||
        line.createdRevision > accepted.revision || line.lastDecreaseRevision > accepted.revision) return [line];
      const remaining = line.quantity - accepted.quantity;
      return remaining > 0 ? [{...line, quantity: remaining, revision: ++nextRevision, lastDecreaseRevision: nextRevision}] : [];
    });
    try {this._save(lines);} catch {this.error = ERROR; return false;}
    this.lines = lines; this.revision = nextRevision + 1; this.error = null; return true;
  }

  async refresh() {
    if (!this.session) return false;
    const epoch = this.epoch, request = ++this._request, snapshot = this.lines.map(line => ({...line}));
    this.pending = true; this.error = null;
    const outcomes = await Promise.all(snapshot.map(async line => {
      try {
        const {identity} = line;
        const response = await this._readProduct(identity.parentCategory, identity.childCategory, identity.code);
        const requested = response?.data?.data;
        const matching = requested?.product?.childProducts?.filter(child => child.id === identity.childProductId && child.code === identity.code);
        if (response?.status === 404) return {...line, item: null, currentSize: null, verification: 'unavailable'};
        if (response?.status !== 200 || requested?.code !== identity.code || !Array.isArray(matching) || matching.length !== 1) throw new Error(REFRESH_ERROR);
        const fresh = {...matching[0], product: {...requested.product, ...matching[0].product}};
        return hydrated(line, fresh);
      } catch {return {...line, item: null, currentSize: null, verification: 'unverified', refreshFailed: true};}
    }));
    return runInAction(() => {
      if (this.epoch !== epoch || this._request !== request) return false;
      this.lines = this.lines.map(line => outcomes.find(value => value.lineKey === line.lineKey && value.revision === line.revision) || line);
      this.error = outcomes.some(line => line.refreshFailed) ? REFRESH_ERROR : null; this.pending = false;
      return this.error === null;
    });
  }
}
